import React, { useEffect, useMemo, useState } from 'react';
import { Chart } from 'react-google-charts';
import * as PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import * as _ from 'lodash';

const useStyles = makeStyles(() => ({
  'chart-container': {
    height: '100%',
    width: '100%',
  },
}));

export default function StackedColumnChart(props) {
  const {
    rawChartData,
    chartBackgroundColor,
    hAxisTitle,
    barArea,
    chartLeftMargin,
    chartRightMargin,
    stackColors,
    legendPosition,
    legendAlignment,
    annotationColor,
    annotationStemLength,
  } = props;
  const [stackedColumnChartData, setStackedColumnChartData] = useState(null);
  const classes = useStyles();

  useEffect(() => {
    if (rawChartData) {
      generateChartData();
    }
  }, [rawChartData]);

  const options = {
    isStacked: true,
    bar: { groupWidth: barArea },
    backgroundColor: chartBackgroundColor,
    chartArea: {
      width: '100%',
      left: chartLeftMargin,
      right: chartRightMargin,
    },
    hAxis: {
      title: hAxisTitle,
    },
    annotations: {
      alwaysOutside: true,
      textStyle: {
        fontSize: 12,
        bold: true,
        color: annotationColor,
      },
      stem: { length: annotationStemLength },
    },
    vAxis: {},
    legend: {
      position: legendPosition,
      alignment: legendAlignment,
      maxLines: 3,
    },
    colors: stackColors,
  };

  const generateChartData = () => {
    const rawData = rawChartData;
    if (rawData?.chartData && typeof rawData?.chartData === 'object') {
      const uniqueStacks = new Set();

      if (rawData?.chartData) {
        Object.values(rawData.chartData).forEach((val) => {
          Object.keys(val).forEach((key) => {
            uniqueStacks.add(key);
          });
        });
        const uniqueStackArray = Array.from(uniqueStacks)?.map(
          (stack) => stack,
        );
        const tempChartData = Object.entries(rawData?.chartData)?.map(
          ([key, value]) => {
            const data = uniqueStackArray?.map((stack) => value[stack] || 0);
            return [key, ...data, _.sum(data)];
          },
        );
        const finalChartData = [
          [
            'Date',
            ...uniqueStackArray?.map((stack) => rawData?.abbrevations[stack]),
            { role: 'annotation' },
          ],
          ...(tempChartData || []),
        ];
        setStackedColumnChartData([...finalChartData]);
      }
    }
  };

  return useMemo(
    () => (
      <>
        {stackedColumnChartData && (
          <div className={classes['chart-container']}>
            <Chart
              chartType="ColumnChart"
              width="100%"
              height="100%"
              data={stackedColumnChartData}
              options={options}
            />
          </div>
        )}
      </>
    ),
    [stackedColumnChartData],
  );
}

StackedColumnChart.propTypes = {
  rawChartData: PropTypes.object,
  chartBackgroundColor: PropTypes.string,
  hAxisTitle: PropTypes.string,
  barArea: PropTypes.string,
  chartLeftMargin: PropTypes.number,
  chartRightMargin: PropTypes.number,
  stackColors: PropTypes.array,
  legendPosition: PropTypes.oneOf(['top', 'bottom']),
  legendAlignment: PropTypes.oneOf(['left', 'right', 'center']),
  annotationColor: PropTypes.string,
  annotationStemLength: PropTypes.number,
};

/**
 * Default propTypes of StackedColumnChart
 * @property chartBackgroundColor {white}
 * @property hAxisTitle {X-Axis}
 * @property barArea {50%}
 * @property chartLeftMargin {50}
 * @property chartRightMargin {10}
 * @property legendPosition {top}
 * @property legendAlignment {center}
 * @property annotationColor {#15607a}
 * @property annotationStemLength {2}
 */

StackedColumnChart.defaultProps = {
  chartBackgroundColor: 'white',
  hAxisTitle: 'X-Axis',
  barArea: '50%',
  chartLeftMargin: 50,
  chartRightMargin: 10,
  legendPosition: 'top',
  legendAlignment: 'center',
  annotationColor: '#15607a',
  annotationStemLength: 2,
};
